const buildPartnerImageUrl = (apiEndPoint, partnerId, imageName) => {
  if (!apiEndPoint || !partnerId || !imageName) {
    return null;
  }

  return `${apiEndPoint}/images/${partnerId}?load=${encodeURIComponent(imageName)}`;
};

const buildChannelLogoUrl = (apiEndPoint, channel) => {
  if (
    !apiEndPoint
    || !channel
    || !channel.id
    || !Array.isArray(channel.logoNames)
    || channel.logoNames.length === 0
  ) {
    return null;
  }

  return `${apiEndPoint}/partners/rtv_channels/${channel.id}/images?load=${encodeURIComponent(channel.logoNames[0])}`;
};

const getPartnerHeroImageName = partner => {
  if (!partner) {
    return null;
  }

  if (partner.heroImageUrl) {
    return partner.heroImageUrl;
  }

  return Array.isArray(partner.galleryImages)
    ? partner.galleryImages.find(Boolean) || null
    : null;
};

export const splitPartnerName = name => {
  const words = String(name || '').trim().split(/\s+/).filter(Boolean);

  if (words.length < 2) {
    return {accent: null, primary: words.join(' ')};
  }

  const splitIndex = Math.ceil(words.length / 2);
  return {
    accent: words.slice(splitIndex).join(' '),
    primary: words.slice(0, splitIndex).join(' ')
  };
};

const createTvLabels = t => ({
  allChannelsLabel: t('CLUB_SKYWORK_TV_ALL_CHANNELS'),
  allChannelsDescription: t('CLUB_SKYWORK_TV_ALL_CHANNELS_DESCRIPTION'),
  availableLabel: t('CLUB_SKYWORK_TV_AVAILABLE'),
  availableDescription: t('CLUB_SKYWORK_TV_AVAILABLE_DESCRIPTION'),
  catalogChoicesEyebrow: t('CLUB_SKYWORK_TV_CATALOG_CHOICES_EYEBROW'),
  catalogChoicesTitle: t('CLUB_SKYWORK_TV_CATALOG_CHOICES_TITLE'),
  channelAriaLabel: t('CLUB_SKYWORK_TV_CHANNELS_ARIA'),
  channelDescription: t('CLUB_SKYWORK_TV_CHANNEL_DESCRIPTION'),
  channelLabel: t('CLUB_SKYWORK_TV_CHANNEL_LABEL'),
  channelsEyebrow: t('CLUB_SKYWORK_TV_CHANNELS_EYEBROW'),
  channelsTitle: t('CLUB_SKYWORK_TV_CHANNELS_TITLE'),
  controlsAriaLabel: t('CLUB_SKYWORK_TV_CONTROLS_ARIA'),
  emptyDescription: t('CLUB_SKYWORK_TV_EMPTY_DESCRIPTION'),
  emptyTitle: t('CLUB_SKYWORK_TV_EMPTY_TITLE'),
  errorTitle: t('CLUB_SKYWORK_TV_ERROR_TITLE'),
  eyebrow: t('CLUB_SKYWORK_TV_EYEBROW'),
  filterHelp: t('CLUB_SKYWORK_TV_FILTER_HELP'),
  intro: t('CLUB_SKYWORK_TV_INTRO'),
  loadingDescription: t('CLUB_SKYWORK_TV_LOADING_DESCRIPTION'),
  loadingTitle: t('CLUB_SKYWORK_TV_LOADING_TITLE'),
  number: t('CLUB_SKYWORK_TV_NUMBER'),
  purchaseDescription: t('CLUB_SKYWORK_TV_PURCHASE_DESCRIPTION'),
  renewalNote: t('CLUB_SKYWORK_TV_RENEWAL_NOTE'),
  retryLabel: t('CLUB_SKYWORK_TV_RETRY'),
  storeLabel: t('CLUB_SKYWORK_ACTION_VIDEO_STORE'),
  storeDescription: t('CLUB_SKYWORK_TV_STORE_DESCRIPTION'),
  storeLoadingLabel: t('CLUB_SKYWORK_TV_STORE_LOADING'),
  title: t('CLUB_SKYWORK_TV_TITLE'),
  titleAccent: t('CLUB_SKYWORK_TV_TITLE_ACCENT'),
  upcomingLabel: t('CLUB_SKYWORK_TV_UPCOMING'),
  upcomingDescription: t('CLUB_SKYWORK_TV_UPCOMING_DESCRIPTION'),
  viewOptionsLabel: t('CLUB_SKYWORK_ACTION_VIEW_OPTIONS')
});

export const createPublicPartnerSkyworkViewModel = ({
  apiEndPoint,
  isTvView,
  partner,
  rtvChannels,
  showClubShowcase,
  tvSectionNumber,
  translate
}) => {
  const t = typeof translate === 'function' ? translate : key => key;
  const tv = createTvLabels(t);
  tv.number = tvSectionNumber || tv.number;

  if (!partner) {
    return {
      channels: [],
      hero: null,
      ribbon: {
        actions: [],
        ariaLabel: t('CLUB_SKYWORK_ACTION_RIBBON_ARIA'),
        sequence: [
          t('CLUB_SKYWORK_RIBBON_BOOK'),
          t('CLUB_SKYWORK_RIBBON_PLAY'),
          t('CLUB_SKYWORK_RIBBON_REPLAY_SHARE')
        ]
      },
      tv
    };
  }

  const channels = (rtvChannels || []).map(channel => ({
    id: channel.id,
    logoUrl: buildChannelLogoUrl(apiEndPoint, channel),
    name: channel.name
  }));
  const partnerNameParts = splitPartnerName(partner.name);
  const channelCount = channels.length;
  const channelCountLabel = channelCount === 1
    ? t('CLUB_SKYWORK_CHANNEL_SINGLE')
    : t('CLUB_SKYWORK_CHANNEL_MULTIPLE', {count: channelCount});
  const showClubSections = !isTvView;
  const hasBookableFields = showClubSections && Boolean(partner.bookableFields);
  const hasCalendarFields = showClubSections && Boolean(partner.calendarFields);
  const hasPlannerFields = hasBookableFields || hasCalendarFields;
  const ribbonActions = [
    {
      action: 'showcase',
      expanded: showClubShowcase,
      id: 'showcase',
      kind: 'showcase',
      label: showClubShowcase
        ? t('CLUB_SKYWORK_ACTION_HIDE_INFO')
        : t('CLUB_SKYWORK_ACTION_INFO'),
      visible: showClubSections
    },
    {
      action: 'affiliations',
      id: 'affiliations',
      kind: 'affiliations',
      label: t('CLUB_SKYWORK_ACTION_AFFILIATIONS'),
      visible: showClubSections
    },
    {
      action: 'shop',
      id: 'shop',
      kind: 'shop',
      label: t('CLUB_SKYWORK_ACTION_SHOP'),
      visible: Boolean(partner.shopActive)
    }
  ].filter(action => action.visible);

  return {
    channels,
    hero: {
      accessItems: [
        t('CLUB_SKYWORK_HERO_VIDEO_ACCESS'),
        t('CLUB_SKYWORK_HERO_LIVE_REPLAY'),
        hasPlannerFields
          ? hasBookableFields
            ? t('CLUB_SKYWORK_HERO_BOOKING_ACCESS')
            : t('CLUB_SKYWORK_HERO_CALENDAR_ACCESS')
          : null
      ].filter(Boolean),
      channelCountLabel,
      description: partner.description
        || partner.extendedDescription
        || t('CLUB_SKYWORK_HERO_DESCRIPTION_FALLBACK'),
      eyebrow: t('CLUB_SKYWORK_HERO_EYEBROW'),
      heroAlt: t('CLUB_SKYWORK_HERO_ALT', {club: partner.name}),
      heroImageUrl: buildPartnerImageUrl(
        apiEndPoint,
        partner.id,
        getPartnerHeroImageName(partner)
      ),
      location: [partner.city, partner.province].filter(Boolean).join(' · '),
      logoAlt: t('CLUB_SKYWORK_LOGO_ALT', {club: partner.name}),
      logoUrl: buildPartnerImageUrl(apiEndPoint, partner.id, partner.logoUrl),
      name: partner.name,
      nameAccent: partnerNameParts.accent,
      namePrimary: partnerNameParts.primary,
      primaryAction: {
        action: 'catalog',
        id: 'catalog',
        label: t('CLUB_SKYWORK_ACTION_VIDEOS')
      },
      secondaryAction: hasPlannerFields
        ? {
          action: 'booking',
          id: 'booking',
          label: hasBookableFields ? t('CLUB_SKYWORK_ACTION_BOOKING') : t('CLUB_SKYWORK_ACTION_CALENDAR')
        }
        : null
    },
    ribbon: {
      actions: ribbonActions,
      ariaLabel: t('CLUB_SKYWORK_ACTION_RIBBON_ARIA'),
      sequence: [
        t('CLUB_SKYWORK_RIBBON_BOOK'),
        t('CLUB_SKYWORK_RIBBON_PLAY'),
        t('CLUB_SKYWORK_RIBBON_REPLAY_SHARE')
      ]
    },
    tv
  };
};
